import {
  Building2, Clock, Users, Globe, Tag, FileText,
  CreditCard, Receipt, Calculator, Crown,
  Bell, Megaphone, Languages,
  Sparkles, MapPin, Code2, Shield, Ticket, Navigation,
} from 'lucide-react';
import { settingsHub } from '../../../styles/tokens';

export interface SettingsCardConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  route: string;
  iconColors: { bg: string; fg: string };
  completionKey: string;
}

export interface SettingsSectionConfig {
  id: string;
  label: string;
  cards: SettingsCardConfig[];
}

export const SETTINGS_SECTIONS: SettingsSectionConfig[] = [
  {
    id: 'business',
    label: 'Your business',
    cards: [
      {
        id: 'profile',
        title: 'Business profile',
        description: 'Name, address, phone, logo, industry, timezone',
        icon: Building2,
        route: '/settings/profile',
        iconColors: { bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
        completionKey: 'profile',
      },
      {
        id: 'hours',
        title: 'Business hours',
        description: 'Weekly schedule, holidays, closed dates',
        icon: Clock,
        route: '/settings/hours',
        iconColors: { bg: settingsHub.iconGreenBg, fg: settingsHub.iconGreenFg },
        completionKey: 'hours',
      },
      {
        id: 'schedule',
        title: 'Schedule',
        description: 'Default view, travel buffer, recurring horizon',
        icon: Clock,
        route: '/settings/schedule',
        iconColors: { bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
        completionKey: 'optional',
      },
      {
        id: 'team',
        title: 'Team & roles',
        description: 'Permissions, commission rules, operator config',
        icon: Users,
        route: '/settings/team',
        iconColors: { bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
        completionKey: 'team',
      },
    ],
  },
  {
    id: 'client_experience',
    label: 'Client experience',
    cards: [
      {
        id: 'booking',
        title: 'Booking page',
        description: 'Slug, branding, hero, reviews, embed widget',
        icon: Globe,
        route: '/settings/booking',
        iconColors: { bg: settingsHub.iconOrangeBg, fg: settingsHub.iconOrangeFg },
        completionKey: 'booking_page',
      },
      {
        id: 'services',
        title: 'Services & pricing',
        description: 'Catalog, dynamic pricing, intro offers',
        icon: Tag,
        route: '/settings/services',
        iconColors: { bg: settingsHub.iconAmberBg, fg: settingsHub.iconAmberFg },
        completionKey: 'services',
      },
      {
        id: 'fields',
        title: 'Client fields',
        description: 'Custom intake fields per industry',
        icon: FileText,
        route: '/settings/fields',
        iconColors: { bg: settingsHub.iconGreenBg, fg: settingsHub.iconGreenFg },
        completionKey: 'optional',
      },
      {
        id: 'promos',
        title: 'Promo codes',
        description: 'Discount codes for bookings and checkout',
        icon: Ticket,
        route: '/settings/promos',
        iconColors: { bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
        completionKey: 'optional',
      },
    ],
  },
  {
    id: 'payments',
    label: 'Payments & billing',
    cards: [
      {
        id: 'payment_setup',
        title: 'Payment setup',
        description: 'Stripe Connect — accept card payments',
        icon: CreditCard,
        route: '/settings/payments',
        iconColors: { bg: settingsHub.iconLimeBg, fg: settingsHub.iconLimeFg },
        completionKey: 'stripe_connect',
      },
      {
        id: 'invoicing',
        title: 'Invoice branding',
        description: 'Logo, accent colour, terms, HST, footer',
        icon: Receipt,
        route: '/settings/invoicing',
        iconColors: { bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
        completionKey: 'invoice_settings',
      },
      {
        id: 'tax',
        title: 'Tax & compliance',
        description: 'HST rates, CRA categories, mileage rates',
        icon: Calculator,
        route: '/settings/tax',
        iconColors: { bg: settingsHub.iconAmberBg, fg: settingsHub.iconAmberFg },
        completionKey: 'optional',
      },
      {
        id: 'subscription',
        title: 'Your subscription',
        description: 'Current plan, upgrade, billing history',
        icon: Crown,
        route: '/settings/subscription',
        iconColors: { bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
        completionKey: 'subscription',
      },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    cards: [
      {
        id: 'notifications',
        title: 'Notifications',
        description: 'Email preferences, auto-replies, reminders',
        icon: Bell,
        route: '/settings/notifications',
        iconColors: { bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
        completionKey: 'optional',
      },
      {
        id: 'marketing',
        title: 'Marketing',
        description: 'Visit recency, birthday, no-show campaigns',
        icon: Megaphone,
        route: '/settings/marketing',
        iconColors: { bg: settingsHub.iconOrangeBg, fg: settingsHub.iconOrangeFg },
        completionKey: 'optional',
      },
      {
        id: 'language',
        title: 'Language',
        description: 'App language, client language, auto-translate',
        icon: Languages,
        route: '/settings/language',
        iconColors: { bg: settingsHub.iconGreenBg, fg: settingsHub.iconGreenFg },
        completionKey: 'optional',
      },
    ],
  },
  {
    id: 'tools',
    label: 'Tools & integrations',
    cards: [
      {
        id: 'auto_features',
        title: 'Auto features',
        description: 'Auto-arrival, auto-text, mileage capture, geofence',
        icon: Navigation,
        route: '/settings/auto-features',
        iconColors: { bg: settingsHub.iconGreenBg, fg: settingsHub.iconGreenFg },
        completionKey: 'optional',
      },
      {
        id: 'ai',
        title: 'AI features',
        description: 'Receipt scanning, smart replies, summaries',
        icon: Sparkles,
        route: '/settings/ai',
        iconColors: { bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
        completionKey: 'optional',
      },
      {
        id: 'directory',
        title: 'Directory listing',
        description: 'Appear on CrewBit Explore, cover photo',
        icon: MapPin,
        route: '/settings/directory',
        iconColors: { bg: settingsHub.iconLimeBg, fg: settingsHub.iconLimeFg },
        completionKey: 'optional',
      },
      {
        id: 'embed',
        title: 'Embed widget',
        description: 'Add booking to your website',
        icon: Code2,
        route: '/settings/embed',
        iconColors: { bg: settingsHub.iconAmberBg, fg: settingsHub.iconAmberFg },
        completionKey: 'optional',
      },
      {
        id: 'data',
        title: 'Data & privacy',
        description: 'Export data, delete account, PIPEDA',
        icon: Shield,
        route: '/settings/data',
        iconColors: { bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
        completionKey: 'optional',
      },
    ],
  },
];

export const REQUIRED_COMPLETION_KEYS = [
  'profile',
  'hours',
  'team',
  'booking_page',
  'services',
  'stripe_connect',
  'invoice_settings',
  'subscription',
] as const;
